import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../models";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../subtitleDescription";
import { CheckboxInput } from "@fidget/common/components/form/checkboxInput";
import { IOrgConfig } from "../../../models/currentUser/OrgConfig";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { logger } from "@fidget/common/utils";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { FieldState, FormState, Validator } from "formstate";
import styles from "./messageSettings.css";
import { EditTextarea } from "@fidget/common/components/form/editTextarea";
import { SettingsForm } from "../settingsForm";
import { EditInput } from "@fidget/common/components/form/editInput";
import { staticConfig } from "@fidget/common/config/static-config";
import { withPermissionFallback } from "../../HOC/withPermissionFallback/withPermissionFallback";
import { validateNumberInRange } from "@fidget/common/utils/validatorsFormState";
import classNames from "classnames";
import { AppPermission } from "@closerplatform/spinner-openapi";
import { useAgentServices } from "../../../models/agentServices";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";

export const MessagesSettings = withPermissionFallback(
  AppPermission.SettingsGeneral,
  observer(() => {
    const {
      handleTypingPreviewChecked,
      handleProfanitiesChecked,
      handleKeyboardShortcutsChecked,
      isLoading,
      isChanged,
      form,
      handleSubmit,
      handleDiscard,
      handleProfanitiesInput,
      handleMessageMaxAllowedCharactersInput
    } = messagesSettingsState();

    const renderTypingPreviewSection = () => {
      return (
        <>
          <div className={commonStyles.title}>
            <L10n id={"settings.messages.title"} />
          </div>
          <div className={commonStyles.section}>
            <SubtitleDescription
              subtitle={intl.formatMessage("settings.messages.preview")}
              description={intl.formatMessage("settings.messages.preview.description")}
            />
            <div className={commonStyles.content}>
              <CheckboxInput
                value={form.$.typingPreviewEnabled.$}
                id="typing_preview_enabled"
                label={intl.formatMessage("settings.messages.preview.checkbox")}
                onChange={handleTypingPreviewChecked}
              />
            </div>
          </div>
        </>
      );
    };

    const renderMessageLimitsSection = () => {
      return (
        <>
          <div className={commonStyles.title}>
            <L10n id={"settings.messages.charactersLimit.title"} />
          </div>
          <div className={commonStyles.section}>
            <SubtitleDescription
              subtitle={intl.formatMessage("settings.messages.charactersLimit.agent.title")}
              description={intl.formatMessage(
                "settings.messages.charactersLimit.agent.description",
                {
                  upperLimit: staticConfig.maxMessageAllowedCharactersForAgentUpperLimit,
                  defaultLimit: staticConfig.maxMessageAllowedCharactersForAgent
                }
              )}
            />
            <div className={commonStyles.content}>
              <EditInput
                className={classNames(styles.input, styles.short)}
                value={form.$.messageMaxAllowedCharactersForAgentInputField.$}
                placeholder={intl.formatMessage(
                  "settings.messages.charactersLimit.agent.placeholder"
                )}
                onChange={handleMessageMaxAllowedCharactersInput(
                  "messageMaxAllowedCharactersForAgentInputField"
                )}
                hasError={form.$.messageMaxAllowedCharactersForAgentInputField.hasError}
                errorHint={form.$.messageMaxAllowedCharactersForAgentInputField.error}
              />
            </div>
          </div>
          <div className={commonStyles.section}>
            <SubtitleDescription
              subtitle={intl.formatMessage("settings.messages.charactersLimit.client.title")}
              description={intl.formatMessage(
                "settings.messages.charactersLimit.client.description",
                {
                  upperLimit: staticConfig.maxMessageAllowedCharactersForClientUpperLimit,
                  defaultLimit: staticConfig.maxMessageAllowedCharactersForClient
                }
              )}
            />
            <div className={commonStyles.content}>
              <EditInput
                className={classNames(styles.input, styles.short)}
                value={form.$.messageMaxAllowedCharactersForClientInputField.$}
                placeholder={intl.formatMessage(
                  "settings.messages.charactersLimit.client.placeholder"
                )}
                onChange={handleMessageMaxAllowedCharactersInput(
                  "messageMaxAllowedCharactersForClientInputField"
                )}
                hasError={form.$.messageMaxAllowedCharactersForClientInputField.hasError}
                errorHint={form.$.messageMaxAllowedCharactersForClientInputField.error}
              />
            </div>
          </div>
        </>
      );
    };

    const renderMessagesBlockingSection = () => {
      return (
        <>
          <div className={commonStyles.title}>
            <L10n id={"settings.messages.blocking"} />
          </div>
          <div className={commonStyles.section}>
            <SubtitleDescription
              subtitle={intl.formatMessage("settings.messages.blocking.title")}
              description={intl.formatMessage("settings.messages.blocking.description")}
            />
            <div className={commonStyles.content}>
              <CheckboxInput
                value={form.$.profanitiesCheckingEnabled.$}
                id="profanities_enabled"
                label={intl.formatMessage("settings.messages.blocking.checkbox")}
                onChange={handleProfanitiesChecked}
              />
            </div>
            {isLoading ? <CloserLoaderComponent /> : renderProfanitiesDictionarySection()}
          </div>
        </>
      );
    };

    const renderProfanitiesDictionarySection = () => {
      return (
        <>
          <SubtitleDescription
            subtitle={intl.formatMessage("settings.messages.profanities.title")}
            description={intl.formatMessage("settings.messages.profanities.description")}
          />
          <div className={commonStyles.content}>{renderAddProfanitiesInput()}</div>
        </>
      );
    };

    const renderAddProfanitiesInput = () => {
      return (
        <EditTextarea
          className={styles["input-textarea"]}
          value={form.$.profanitiesInputField.$}
          placeholder={intl.formatMessage("settings.messages.profanities.placeholder")}
          onChange={handleProfanitiesInput}
        />
      );
    };

    const renderKeyboardShortcutsSection = () => {
      return (
        <>
          <div className={commonStyles.title}>
            <L10n id={"settings.messages.shortcuts"} />
          </div>
          <div className={commonStyles.section}>
            <SubtitleDescription
              description={intl.formatMessage("settings.messages.shortcuts.description")}
            />
            <div className={commonStyles.content}>
              <CheckboxInput
                value={form.$.keyboardShortcutsEnabled.$}
                id="shortcuts_enabled"
                label={intl.formatMessage("settings.messages.shortcuts.checkbox")}
                onChange={handleKeyboardShortcutsChecked}
              />
            </div>
          </div>
        </>
      );
    };

    const render = () => {
      return (
        <SettingsForm
          disabled={!isChanged}
          onDiscard={handleDiscard}
          onSubmit={handleSubmit}
          saveSectionClassName={styles.save}
        >
          {renderTypingPreviewSection()}
          {renderMessageLimitsSection()}
          {renderMessagesBlockingSection()}
          {renderKeyboardShortcutsSection()}
        </SettingsForm>
      );
    };

    return render();
  })
);

type FormFields = {
  typingPreviewEnabled: FieldState<boolean>;
  profanitiesCheckingEnabled: FieldState<boolean>;
  profanitiesInputField: FieldState<string>;
  messageMaxAllowedCharactersForAgentInputField: FieldState<string | number>;
  messageMaxAllowedCharactersForClientInputField: FieldState<string | number>;
  keyboardShortcutsEnabled: FieldState<boolean>;
};

type MessageLimitingFieldsKeys = keyof Pick<
  FormFields,
  "messageMaxAllowedCharactersForAgentInputField" | "messageMaxAllowedCharactersForClientInputField"
>;

const withEmptyIsValid = (validator: Validator<number>) => (value: string | number) =>
  value === "" ? "" : validator(Number(value));

const createForm = (config: IOrgConfig, profanitiesInput = ""): FormState<FormFields> => {
  return new FormState<FormFields>({
    typingPreviewEnabled: new FieldState(config.typingPreviewEnabled),
    profanitiesCheckingEnabled: new FieldState(!!config.profanitiesCheckingEnabled),
    profanitiesInputField: new FieldState(profanitiesInput),
    messageMaxAllowedCharactersForAgentInputField: new FieldState<string | number>(
      config.messageMaxAllowedCharactersForAgent ?? staticConfig.maxMessageAllowedCharactersForAgent
    ).validators(
      withEmptyIsValid(
        validateNumberInRange(0, staticConfig.maxMessageAllowedCharactersForAgentUpperLimit)
      )
    ),
    messageMaxAllowedCharactersForClientInputField: new FieldState<string | number>(
      config.messageMaxAllowedCharactersForClient ??
        staticConfig.maxMessageAllowedCharactersForClient
    ).validators(
      withEmptyIsValid(
        validateNumberInRange(0, staticConfig.maxMessageAllowedCharactersForClientUpperLimit)
      )
    ),
    keyboardShortcutsEnabled: new FieldState(!!config.keyboardShortcutsEnabled),
  });
};

type TextInputHandler<T> = (fieldName: T) => (e: React.FormEvent<HTMLInputElement>) => void;

interface State {
  isLoading: boolean;
  isChanged: boolean;
  form: FormState<FormFields>;
  handleSubmit: () => void;
  handleDiscard: () => void;
  handleProfanitiesChecked: (value: boolean) => void;
  handleKeyboardShortcutsChecked: (value: boolean) => void;
  handleTypingPreviewChecked: (value: boolean) => void;
  handleProfanitiesInput: (e: React.FormEvent<HTMLTextAreaElement>) => void;
  handleMessageMaxAllowedCharactersInput: TextInputHandler<MessageLimitingFieldsKeys>;
}

const messagesSettingsState = (): State => {
  const { spinnerService } = useAgentServices();
  const { uiStore, roomStore, currentUserStore, profanityStore } = useStore();
  const orgConfig = currentUserStore.orgConfig!;

  const [profanities, setProfanities] = useState<Array<string>>();
  const [form] = useState<FormState<FormFields>>(createForm(orgConfig));
  const [isLoading, setIsLoading] = useState(true);
  const [isChanged, setIsChanged] = useState(false);

  const loadProfanities = async () => {
    try {
      const { expressions } = await spinnerService.getProfanities();
      setProfanities(expressions);
      form.$.profanitiesInputField.reset(expressions.join(","));
    } catch (e) {
      if ((e as Response).status !== HttpStatusCode.NOT_FOUND) {
        logger.error({ message: "Couldn't load profanities", error: e });
        uiStore.addGenericErrorToast();
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProfanities();
  }, []);

  const handleTypingPreviewChecked = (checked: boolean) => {
    form.$.typingPreviewEnabled.onChange(checked);
    setIsChanged(true);
  };

  const handleProfanitiesChecked = (checked: boolean) => {
    form.$.profanitiesCheckingEnabled.onChange(checked);
    setIsChanged(true);
  };

  const handleProfanitiesInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    form.$.profanitiesInputField.reset((e.target as HTMLTextAreaElement).value);
    setIsChanged(true);
  };

  const parseInputToProfanities = (): string[] => {
    const input = form.$.profanitiesInputField.$;
    return input ? input.split(",").map((word) => word.trim()) : [];
  };

  const handleMessageMaxAllowedCharactersInput =
    (fieldName: MessageLimitingFieldsKeys) => (e: React.FormEvent<HTMLInputElement>) => {
      const value = (e.target as HTMLInputElement).value;

      const formField = form.$[fieldName];

      const valueAsInt = parseInt(value);
      const isInteger = !isNaN(valueAsInt);

      if (value === "" || isInteger) {
        formField.reset(isInteger ? valueAsInt : value);

        setIsChanged(true);
        formField.validate();
      }
    };

  const handleKeyboardShortcutsChecked = (checked: boolean) => {
    form.$.keyboardShortcutsEnabled.onChange(checked);
    setIsChanged(true);
  };

  const saveOrgConfig = async () => {
    const messageMaxAllowedCharactersForAgent = Number(
      form.$.messageMaxAllowedCharactersForAgentInputField.$ ||
        staticConfig.maxMessageAllowedCharactersForAgent
    );
    const messageMaxAllowedCharactersForClient = Number(
      form.$.messageMaxAllowedCharactersForClientInputField.$ ||
        staticConfig.maxMessageAllowedCharactersForClient
    );

    await orgConfig.update({
      profanitiesCheckingEnabled: form.$.profanitiesCheckingEnabled.$,
      keyboardShortcutsEnabled: form.$.keyboardShortcutsEnabled.$,
      typingPreviewEnabled: form.$.typingPreviewEnabled.$,
      messageMaxAllowedCharactersForAgent,
      messageMaxAllowedCharactersForClient
    });
    roomStore.rooms.forEach((room) => {
      room.clearTypingUsers();
    });
  };

  const saveProfanities = async () => {
    if (form.$.profanitiesCheckingEnabled.$ && profanities) {
      profanityStore.setProfanities(profanities);
    }

    const updatedProfanities = parseInputToProfanities();
    if (profanities) {
      await spinnerService.updateProfanities({ expressions: updatedProfanities });
    } else {
      await spinnerService.createProfanities({ expressions: updatedProfanities });
    }
    setProfanities(updatedProfanities);
    profanityStore.setProfanities(updatedProfanities);

    form.$.profanitiesInputField.reset(updatedProfanities.join(","));
  };

  const handleSubmit = async () => {
    const validate = await form.validate();
    if (validate.hasError) {
      uiStore.addGenericErrorToast();
      return;
    }

    try {
      await saveOrgConfig();
      await saveProfanities();

      setIsChanged(false);
    } catch (err) {
      logger.error({ message: "Couldn't save settings", error: err });
      uiStore.addGenericErrorToast();
    }
  };

  const handleDiscard = () => {
    form.$.profanitiesInputField.reset(profanities?.join(","));
    form.$.profanitiesCheckingEnabled.reset(!!orgConfig.profanitiesCheckingEnabled);
    form.$.keyboardShortcutsEnabled.reset(!!orgConfig.keyboardShortcutsEnabled);
    form.$.typingPreviewEnabled.reset(orgConfig.typingPreviewEnabled);
    form.$.messageMaxAllowedCharactersForAgentInputField.reset(
      orgConfig.messageMaxAllowedCharactersForAgent
    );
    form.$.messageMaxAllowedCharactersForClientInputField.reset(
      orgConfig.messageMaxAllowedCharactersForClient
    );
    setIsChanged(false);
  };

  return {
    handleTypingPreviewChecked,
    handleProfanitiesChecked,
    handleKeyboardShortcutsChecked,
    handleProfanitiesInput,
    handleMessageMaxAllowedCharactersInput,
    isLoading,
    form,
    handleDiscard,
    handleSubmit,
    isChanged
  };
};
